// /hud (decision #2): a second, chromeless route for the floating desktop HUD window
// (clients/desktop/src/hud.rs). Recording itself keeps running in the main window's webview
// (decision #3) — this page is a remote control plus indicator, driven by grackle://hud/state
// broadcasts and able to raise the same record/stop and record/pause events the tray and the
// global hotkeys already use. In a plain browser there is no Tauri bridge to listen to, so it
// says that rather than rendering dead controls.
import { useEffect, useState } from "react";
import { Button, RecPill, RecorderBar, UploadStatus } from "@grackle/ui";
import {
  EVT_HUD_STATE,
  EVT_MEETING_KEEP,
  EVT_RECORD_PAUSE,
  EVT_RECORD_STOP,
  emitDesktop,
  isDesktop,
  listenDesktopPayload,
  recorderHotkeys,
  type HudState,
} from "../desktop";
import { hudRecLabel } from "../recorder/meetingCapture";
import "./hud.css";

export function formatHudElapsed(ms: number): string {
  const totalSeconds = Math.max(0, Math.floor(ms / 1000));
  const m = Math.floor(totalSeconds / 60);
  const s = totalSeconds % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
}

export default function HudPage() {
  const [state, setState] = useState<HudState | null>(null);
  const desktop = isDesktop();

  useEffect(() => {
    document.body.classList.add("hud-body");
    document.documentElement.classList.add("hud-html");
    return () => {
      document.body.classList.remove("hud-body");
      document.documentElement.classList.remove("hud-html");
    };
  }, []);

  useEffect(() => {
    if (!desktop) return;
    let unsub: (() => void) | undefined;
    void listenDesktopPayload<HudState>(EVT_HUD_STATE, setState).then((u) => {
      unsub = u;
    });
    return () => unsub?.();
  }, [desktop]);

  if (!desktop) {
    return (
      <div className="hud-page hud-page--web">
        <p>The recording bar works in the Grackle desktop app.</p>
      </div>
    );
  }

  const hotkeys = recorderHotkeys();
  const upload = state?.upload;

  // F8 / FR-RD15: the HUD is the on-screen recording indicator. There is deliberately no hide
  // control here (and `hud_hide` refuses while recording); the window can only be moved.
  const meeting = state?.meeting;
  return (
    <div className="hud-page" data-theme="dark">
      {meeting ? (
        <RecPill label={hudRecLabel(meeting.app)} />
      ) : null}
      {meeting && meeting.closingIn !== null ? (
        <span role="status" className="hud-closing">
          Meeting window closed — stopping in {meeting.closingIn}s
          <Button size="sm" variant="secondary" onClick={() => void emitDesktop(EVT_MEETING_KEEP)}>
            Keep recording
          </Button>
        </span>
      ) : null}
      <RecorderBar
        time={formatHudElapsed(state?.elapsedMs ?? 0)}
        paused={state?.paused}
        level={state?.level}
        pauseHotkey={hotkeys.pause}
        stopHotkey={hotkeys.startStop}
        onPause={() => void emitDesktop(EVT_RECORD_PAUSE)}
        onStop={() => void emitDesktop(EVT_RECORD_STOP)}
      />
      {upload ? <UploadStatus plain state={upload.state} progress={upload.partsCut > 0 ? upload.partsAccepted / upload.partsCut : 0} /> : null}
    </div>
  );
}
